import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useTranslation from '~/app/hooks/useTranslation';
import SearchInput from '~/app/modules/form/Search';
import MForm from '~/app/modules/form';
import Field from '~/app/modules/form/Field';
import Select from '~/app/modules/form/Select';
import Text from '~/app/modules/form/Text';
import useForm from '~/app/modules/form/useForm';
import {
    useCostCategories,
    useCustomers,
    useProjects,
    useWorkTypes,
} from '~/app/data/statuses/issue';
import { useStoreActions, useStoreState } from '~/store';
import { AllUsers } from '~/types';
import { API } from '~/app/routes';
import { WorkOrderDetail, WorkOrderStatuses, AdministrativeFormValues } from '~/workorder/types';
import {
    EDIT_WORK_ORDER_ADMIN_FORM,
    EditWorkOrderAdministrativeFormValues,
    applyCostCategoryCascade,
    applyCustomerCascade,
    canEditMmFields,
    canEditResponsiblePersonnel,
    detailToAdministrativeFormValues,
    editFormValuesToAdministrative,
    findCostCategory,
    findCustomer,
    administrativeCascadeToFormPatch,
    hasMaterialsForCostCategory,
    shouldShowChargedDepartment,
    shouldShowInvoiceAcceptance,
    shouldShowSorting,
    shouldPromptWorkTypeChangeOnSelect,
    shouldRequireInvoiceApprover,
} from '../adminFormHelper';
import { resolveOptionLabel } from '../helper';
import classes from './style.module.scss';

export type AdministrativeEditFormProps = {
    data: WorkOrderDetail;
    departmentLabel: string | null;
    initialAdministrative: ReturnType<typeof detailToAdministrativeFormValues>;
    workTypeFieldKey: number;
    costCategoryFieldKey: number;
    onWorkTypeChangeAttempt: (newWorkType: number) => void;
    onWorkTypeChangeWithoutPrompt: () => void;
    shouldSuppressWorkTypePrompt: (newWorkType: number) => boolean;
    onCostCategoryBlocked: () => void;
};

const LabeledReadOnly: React.FC<{ label: React.ReactNode; value: string | null | undefined }> = ({
    label,
    value,
}) => (
    <Field className={classes['input-container']} label={label}>
        <div className={classes.readOnlyValueBox}>{value?.trim() || '-'}</div>
    </Field>
);

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
    children,
    required = false,
}) => (
    <div>
        {children}
        {required && <span className={classes['required-mark']}>*</span>}
    </div>
);

const AdministrativeEditForm: React.FC<AdministrativeEditFormProps> = ({
    data,
    departmentLabel,
    initialAdministrative,
    workTypeFieldKey,
    costCategoryFieldKey,
    onWorkTypeChangeAttempt,
    onWorkTypeChangeWithoutPrompt,
    shouldSuppressWorkTypePrompt,
    onCostCategoryBlocked,
}) => {
    const { t } = useTranslation('workorder');
    const { t: ti } = useTranslation('issue');

    const { values: formValues, fieldEvents, setValue } = useForm<EditWorkOrderAdministrativeFormValues>({
        formName: EDIT_WORK_ORDER_ADMIN_FORM,
    });

    const [invoiceApproverSearchKey, setInvoiceApproverSearchKey] = useState(0);
    const isFirstInvoiceApproverSync = useRef(true);

    const setIsWorkOrderFormActive = useStoreActions(actions => actions.issue.setIsWorkOrderFormActive);
    const setDisplayCustomer = useStoreActions(actions => actions.issue.setDisplayCustomer);
    const setDisplayProject = useStoreActions(actions => actions.issue.setDisplayProject);
    const isWorkOrderFormActive = useStoreState(state => state.issue.isWorkOrderFormActive);
    const displayCustomer = useStoreState(state => state.issue.displayCustomer);
    const displayProject = useStoreState(state => state.issue.displayProject);

    const workOrderDepartments = useStoreState(state => state.workOrder.departments ?? []);
    const workTypes = useStoreState(state => state.issue.workTypes);
    const costCategories = useStoreState(state => state.issue.costCategories);
    const customers = useStoreState(state => state.issue.customers);
    const projects = useStoreState(state => state.projects.list);
    const appDepartments = useStoreState(state => state.app.appData.departments);

    const workTypeOptions = useWorkTypes(workTypes);
    const costCategoryOptions = useCostCategories(costCategories);
    const customerOptions = useCustomers(customers);
    const projectOptions = useProjects(projects);

    const sortingTypes = useMemo(() => [
        { text: t('detail.sortingTypeOptions.0'), value: 0 },
        { text: t('detail.sortingTypeOptions.1'), value: 1 },
    ], [t]);

    useEffect(() => {
        setIsWorkOrderFormActive(true);
        return () => {
            setIsWorkOrderFormActive(false);
            setDisplayCustomer(false);
            setDisplayProject(false);
        };
    }, [setIsWorkOrderFormActive, setDisplayCustomer, setDisplayProject]);

    useEffect(() => {
        const selectedCategoryId = Number(formValues?.costCategory);
        const selectedCategory = costCategories.find(({ id }) => Number(id) === selectedCategoryId);
        setDisplayCustomer(isWorkOrderFormActive && !!selectedCategory?.displayCustomer);
    }, [costCategories, formValues?.costCategory, isWorkOrderFormActive, setDisplayCustomer]);

    useEffect(() => {
        const selectedCustomerId = Number(formValues?.customer);
        const selectedCustomer = customers.find(({ id }) => Number(id) === selectedCustomerId);
        setDisplayProject(isWorkOrderFormActive && !!selectedCustomer?.displayProject);
    }, [customers, formValues?.customer, isWorkOrderFormActive, setDisplayProject]);

    useEffect(() => {
        const approverId = Number(formValues?.invoiceAcceptanceUserId ?? 0);
        if (isFirstInvoiceApproverSync.current) {
            isFirstInvoiceApproverSync.current = false;
            if (approverId === 0 && String(data.invoiceAcceptanceUser ?? '').trim()) {
                return;
            }
        }
        if (approverId === 0) {
            setInvoiceApproverSearchKey(key => key + 1);
        }
    }, [formValues?.invoiceAcceptanceUserId, data.invoiceAcceptanceUser]);

    const responsibleOptions = useMemo(() => {
        const departmentId = Number(data.departmentId ?? 0);
        const users = workOrderDepartments.find(d => d.id === departmentId)?.users ?? [];
        return users.map(user => ({ text: user.name, value: user.id }));
    }, [workOrderDepartments, data.departmentId]);

    const chargedDepartmentOptions = useMemo(
        () => appDepartments?.map(dept => ({ text: dept.name, value: dept.id })) ?? [],
        [appDepartments],
    );

    const selectedCategory = useMemo(
        () => findCostCategory(costCategories, formValues?.costCategory),
        [costCategories, formValues?.costCategory],
    );

    const selectedCustomer = useMemo(
        () => findCustomer(customers, formValues?.customer),
        [customers, formValues?.customer],
    );

    const mmDisabled = !canEditMmFields(data.status);
    const responsibleDisabled = !canEditResponsiblePersonnel(data.status);

    const showChargedDepartment = shouldShowChargedDepartment(selectedCategory);
    const showSorting = displayCustomer && shouldShowSorting(selectedCustomer);
    const showInvoice = displayCustomer && shouldShowInvoiceAcceptance(selectedCustomer);

    const applyAdminPatchToForm = useCallback((admin: AdministrativeFormValues) => {
        const patch = administrativeCascadeToFormPatch(admin);
        Object.entries(patch).forEach(([key, value]) => {
            setValue(key as keyof EditWorkOrderAdministrativeFormValues, value);
        });
    }, [setValue]);

    const handleCostCategoryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const categoryId = Number(e.target.value) > 0 ? Number(e.target.value) : null;
        const currentCategoryId = Number(formValues?.costCategory ?? 0) > 0
            ? Number(formValues?.costCategory)
            : null;

        if (
            categoryId !== currentCategoryId
            && hasMaterialsForCostCategory(data.materials ?? [], currentCategoryId)
        ) {
            onCostCategoryBlocked();
            return;
        }

        fieldEvents.onChange(e);
        const prevAdmin = editFormValuesToAdministrative(formValues as EditWorkOrderAdministrativeFormValues);
        const nextAdmin = applyCostCategoryCascade(
            prevAdmin,
            categoryId,
            costCategories,
            customers,
            initialAdministrative,
        );
        applyAdminPatchToForm(nextAdmin);
    }, [
        fieldEvents,
        formValues,
        data.materials,
        costCategories,
        customers,
        initialAdministrative,
        applyAdminPatchToForm,
        onCostCategoryBlocked,
    ]);

    const handleCustomerChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        fieldEvents.onChange(e);
        const customerId = Number(e.target.value) > 0 ? Number(e.target.value) : null;
        const prevAdmin = editFormValuesToAdministrative(formValues as EditWorkOrderAdministrativeFormValues);
        const nextAdmin = applyCustomerCascade(
            prevAdmin,
            customerId,
            initialAdministrative,
            costCategories,
            customers,
        );
        applyAdminPatchToForm(nextAdmin);
    }, [
        fieldEvents,
        formValues,
        initialAdministrative,
        costCategories,
        customers,
        applyAdminPatchToForm,
    ]);

    const handleWorkTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const newWorkType = Number(e.target.value);
        const suppressed = shouldSuppressWorkTypePrompt(newWorkType);
        if (suppressed) {
            fieldEvents.onChange(e);
            return;
        }
        if (shouldPromptWorkTypeChangeOnSelect(data, newWorkType)) {
            onWorkTypeChangeAttempt(newWorkType);
            return;
        }
        onWorkTypeChangeWithoutPrompt();
        fieldEvents.onChange(e);
    }, [data, fieldEvents, onWorkTypeChangeAttempt, onWorkTypeChangeWithoutPrompt, shouldSuppressWorkTypePrompt]);

    const handleInvoiceApproverSelect = useCallback((user: AllUsers) => {
        setValue('invoiceAcceptanceUserId', user.id);
    }, [setValue]);

    if (!formValues) {
        return null;
    }

    return (
        <MForm className={classes.form}>
            <LabeledReadOnly label={t('detail.department')} value={departmentLabel} />
            <LabeledReadOnly label={t('detail.createdBy')} value={data.createdBy?.name} />
            {data.status === WorkOrderStatuses.APPROVED && (
                <LabeledReadOnly label={t('detail.approvedBy')} value={data.approvedBy?.name} />
            )}

            {responsibleDisabled ? (
                <LabeledReadOnly label={t('detail.responsible')} value={data.personnel?.name} />
            ) : (
            <Select
                key={`personnel-${data.departmentId}`}
                className={classes['input-container']}
                label={(
                    <FieldLabel required={canEditResponsiblePersonnel(data.status)}>
                        {t('detail.responsible')}
                    </FieldLabel>
                )}
                name="personnelId"
                placeholder={t('detail.placeholders.responsible')}
                options={responsibleOptions}
                icon="keyboard_arrow_down"
                onChange={fieldEvents.onChange}
                defaultValue={String(formValues.personnelId ?? 0)}
                showEmptyOption
                emptyOptionValue={0}
            />
            )}

            <Text
                className={classes['input-container']}
                label={t('detail.workshop')}
                name="repairLocation"
                placeholder={t('detail.placeholders.workshop')}
                maxLength={50}
                defaultValue={String(formValues.repairLocation ?? '').trim()}
                readOnly={mmDisabled}
                disabled={mmDisabled}
                {...fieldEvents}
            />

            {mmDisabled ? (
                <LabeledReadOnly
                    label={(
                        <FieldLabel required>
                            {t('detail.workType')}
                        </FieldLabel>
                    )}
                    value={resolveOptionLabel(workTypeOptions, formValues.workType)}
                />
            ) : (
            <Select
                key={`workType-${workTypeFieldKey}`}
                className={classes['input-container']}
                label={(
                    <FieldLabel required>
                        {t('detail.workType')}
                    </FieldLabel>
                )}
                name="workType"
                placeholder={ti('workType.placeholder')}
                options={workTypeOptions}
                icon="keyboard_arrow_down"
                onChange={handleWorkTypeChange}
                defaultValue={formValues.workType}
            />
            )}

            {mmDisabled ? (
                <LabeledReadOnly
                    label={(
                        <FieldLabel required>
                            {t('detail.costCategory')}
                        </FieldLabel>
                    )}
                    value={resolveOptionLabel(costCategoryOptions, formValues.costCategory)}
                />
            ) : (
            <Select
                key={`costCategory-${costCategoryFieldKey}`}
                className={classes['input-container']}
                label={(
                    <FieldLabel required>
                        {t('detail.costCategory')}
                    </FieldLabel>
                )}
                name="costCategory"
                placeholder={ti('costCategory.placeholder')}
                options={costCategoryOptions}
                icon="keyboard_arrow_down"
                onChange={handleCostCategoryChange}
                defaultValue={formValues.costCategory}
                showEmptyOption
                emptyOptionValue={0}
            />
            )}

            {showChargedDepartment && (
                mmDisabled ? (
                    <LabeledReadOnly
                        label={(
                            <FieldLabel required>
                                {t('detail.chargedDepartment')}
                            </FieldLabel>
                        )}
                        value={resolveOptionLabel(chargedDepartmentOptions, formValues.chargedDepartmentId)}
                    />
                ) : (
                <Select
                    className={classes['input-container']}
                    label={(
                        <FieldLabel required>
                            {t('detail.chargedDepartment')}
                        </FieldLabel>
                    )}
                    name="chargedDepartmentId"
                    placeholder={t('detail.placeholders.chargedDepartment')}
                    options={chargedDepartmentOptions}
                    icon="keyboard_arrow_down"
                    onChange={fieldEvents.onChange}
                    defaultValue={formValues.chargedDepartmentId}
                    showEmptyOption
                    emptyOptionValue={0}
                />
                )
            )}

            {displayCustomer && (
                mmDisabled ? (
                    <LabeledReadOnly
                        label={(
                            <FieldLabel required>
                                {t('detail.customer')}
                            </FieldLabel>
                        )}
                        value={resolveOptionLabel(customerOptions, formValues.customer)}
                    />
                ) : (
                    <Select
                        className={classes['input-container']}
                        label={(
                            <FieldLabel required>
                                {t('detail.customer')}
                            </FieldLabel>
                        )}
                        name="customer"
                        placeholder={ti('customer.placeholder')}
                        options={customerOptions}
                        icon="keyboard_arrow_down"
                        onChange={handleCustomerChange}
                        defaultValue={formValues.customer}
                        showEmptyOption
                        emptyOptionValue={0}
                    />
                )
            )}

            {displayProject && (
                mmDisabled ? (
                    <LabeledReadOnly
                        label={(
                            <FieldLabel required>
                                {t('detail.project')}
                            </FieldLabel>
                        )}
                        value={resolveOptionLabel(projectOptions, formValues.project)}
                    />
                ) : (
                    <Select
                        className={classes['input-container']}
                        label={(
                            <FieldLabel required>
                                {t('detail.project')}
                            </FieldLabel>
                        )}
                        name="project"
                        placeholder={ti('project.placeholder')}
                        options={projectOptions}
                        icon="keyboard_arrow_down"
                        onChange={fieldEvents.onChange}
                        defaultValue={formValues.project}
                        showEmptyOption
                        emptyOptionValue={0}
                    />
                )
            )}

            {showSorting && (
                mmDisabled ? (
                    <LabeledReadOnly
                        label={(
                            <FieldLabel required>
                                {t('detail.sortingType')}
                            </FieldLabel>
                        )}
                        value={resolveOptionLabel(sortingTypes, formValues.sortingType)}
                    />
                ) : (
                <Select
                    className={classes['input-container']}
                    label={(
                        <FieldLabel required>
                            {t('detail.sortingType')}
                        </FieldLabel>
                    )}
                    name="sortingType"
                    placeholder={ti('sortingType.placeholder')}
                    options={sortingTypes}
                    icon="keyboard_arrow_down"
                    onChange={fieldEvents.onChange}
                    defaultValue={formValues.sortingType}
                />
                )
            )}

            {showInvoice && (
                mmDisabled ? (
                    <LabeledReadOnly
                        label={t('detail.approvalInvoice')}
                        value={data.invoiceAcceptanceUser}
                    />
                ) : (
                    <SearchInput<AllUsers>
                        key={`invoice-approver-${invoiceApproverSearchKey}`}
                        className={classes['input-container']}
                        inputClassName={classes['input-container-input']}
                        label={(
                            <FieldLabel required={shouldRequireInvoiceApprover(selectedCustomer)}>
                                {t('detail.approvalInvoice')}
                            </FieldLabel>
                        )}
                        placeholder={data.invoiceAcceptanceUser ? data.invoiceAcceptanceUser : t('detail.placeholders.invoiceApprover')}
                        searchUrl={API.allUsers}
                        searchKey="name"
                        renderItem={user => user.name}
                        onSelect={handleInvoiceApproverSelect as any}
                        iconPosition="left"
                    />
                )
            )}
        </MForm>
    );
};

export default AdministrativeEditForm;
